import CSS from "csstype";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Balance } from "~src/components/Balance";
import {
  BombAnimation,
  BombAnimationEvent,
} from "~src/components/BombAnimation";
import { Center } from "~src/components/Center";
import { Chat } from "~src/components/Chat";
import { Hand } from "~src/components/Hand";
import { LeftPerson } from "~src/components/LeftPerson";
import { LeftTurnSignal } from "~src/components/LeftTurnSignal";
import { Multiplier } from "~src/components/Multiplier";
import { Options } from "~src/components/Options";
import { Results } from "~src/components/Results";
import { RightPerson } from "~src/components/RightPerson";
import { RightTurnSignal } from "~src/components/RightTurnSignal";
import { getHandForPosition } from "~src/components/turnDisplay";
import { userCheckState } from "~src/user/apigen/checkState";
import { IGetStateCard, IUserGetStateReturn, useUserGetState } from "~src/user/apigen/getState";

type RoomProps = {
  leaveRoom: () => void;
};

const dragonCardRevealDelayMs = 1750;

export const Room: React.FC<RoomProps> = ({ leaveRoom }) => {
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const { data: serverState, mutate: fetchState } = useUserGetState();
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [optimisticAction, setOptimisticAction] = useState<OptimisticAction | null>(null);
  const [queuedBombEvents, setQueuedBombEvents] = useState<BombAnimationEvent[]>([]);
  const [activeBombEvent, setActiveBombEvent] = useState<BombAnimationEvent | null>(null);
  const [hideCenterCardsForAnimation, setHideCenterCardsForAnimation] = useState<boolean>(false);
  const [pageVisible, setPageVisible] = useState<boolean>(document.visibilityState === "visible");
  const roomVersionRef = useRef<number | null>(null);
  const seenBombKeysRef = useRef<Set<string>>(new Set());
  const state = getDisplayedState(serverState, optimisticAction);
  const pollInterval = getPollInterval(state, optimisticAction !== null, pageVisible);

  const toggleCard = useCallback(
    (selectedID: number) => {
      let updatedCards;

      if (selectedCards.includes(selectedID)) {
        updatedCards = selectedCards.filter(id => id !== selectedID);
      } else {
        updatedCards = [...selectedCards, selectedID];
      }

      setSelectedCards(updatedCards);
    },
    [selectedCards],
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      setPageVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    let active = true;

    const runCheck = () => {
      void (async () => {
        try {
          const nextState = await userCheckState();
          if (!active) {
            return;
          }

          if (roomVersionRef.current === null) {
            roomVersionRef.current = nextState.state;
            return;
          }

          if (nextState.state !== roomVersionRef.current) {
            roomVersionRef.current = nextState.state;
            await fetchState();
          }
        } catch (e) {
          // Ignore transient polling failures and try again on the next tick.
        }
      })();
    };

    runCheck();
    const interval = window.setInterval(runCheck, pollInterval);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [fetchState, pollInterval]);

  useEffect(() => {
    if (serverState === undefined || optimisticAction === null) {
      return;
    }

    if (getStateFingerprint(serverState) !== optimisticAction.baseFingerprint) {
      setOptimisticAction(null);
    }
  }, [optimisticAction, serverState]);

  useEffect(() => {
    if (activeBombEvent !== null || queuedBombEvents.length === 0) {
      return;
    }

    const [nextBombEvent] = queuedBombEvents;
    if (nextBombEvent === undefined) {
      return;
    }

    setActiveBombEvent(nextBombEvent);
    setQueuedBombEvents(currentQueue => currentQueue.slice(1));
  }, [activeBombEvent, queuedBombEvents]);

  useEffect(() => {
    if (activeBombEvent === null) {
      setHideCenterCardsForAnimation(false);
      return;
    }

    setHideCenterCardsForAnimation(true);

    const revealTimeout = window.setTimeout(() => {
      setHideCenterCardsForAnimation(false);
    }, dragonCardRevealDelayMs);

    return () => {
      window.clearTimeout(revealTimeout);
    };
  }, [activeBombEvent]);

  const applyPendingTurn = useCallback(
    (cardIDs: number[]) => {
      if (serverState === undefined || serverState.position !== serverState.turn) {
        return;
      }

      const selectedCardIDs = new Set(cardIDs);
      const playedCards = serverState.cards.filter(card => selectedCardIDs.has(card.id));
      const baseFingerprint = getStateFingerprint(serverState);

      if (cardIDs.length === 0) {
        setOptimisticAction({ kind: "pass", baseFingerprint });
        return;
      }

      setOptimisticAction({ kind: "play", cards: playedCards, baseFingerprint });
    },
    [serverState],
  );

  const applyPendingTakeMiddleCards = useCallback(() => {
    if (serverState === undefined) {
      return;
    }

    setOptimisticAction({
      kind: "take",
      baseFingerprint: getStateFingerprint(serverState),
    });
  }, [serverState]);

  const applyPendingPlayAgain = useCallback(() => {
    if (serverState === undefined) {
      return;
    }

    setOptimisticAction({
      kind: "playAgain",
      baseFingerprint: getStateFingerprint(serverState),
    });
  }, [serverState]);

  const enqueueBombEvent = useCallback((cards: IGetStateCard[]) => {
    if (cards.length === 0) {
      return;
    }

    if (!isBombCards(cards)) {
      return;
    }

    const bombKey = getBombKey(cards);
    if (seenBombKeysRef.current.has(bombKey)) {
      return;
    }

    seenBombKeysRef.current.add(bombKey);
    setQueuedBombEvents(currentQueue => [
      ...currentQueue,
      {
        id: bombKey,
        cards,
      },
    ]);
  }, []);

  useEffect(() => {
    if (optimisticAction?.kind !== "play") {
      return;
    }

    enqueueBombEvent(optimisticAction.cards);
  }, [enqueueBombEvent, optimisticAction]);

  useEffect(() => {
    if (serverState === undefined || serverState.lastTwoTurns.length === 0) {
      return;
    }

    const candidateBombTurn = getLatestBombCandidate(serverState.lastTwoTurns);
    if (candidateBombTurn === null) {
      return;
    }

    enqueueBombEvent(candidateBombTurn.cards);
  }, [enqueueBombEvent, serverState]);

  const revalidateState = useCallback(async () => {
    await fetchState();
  }, [fetchState]);

  const revertOptimisticState = useCallback(async () => {
    setOptimisticAction(null);
    await fetchState();
  }, [fetchState]);

  const handleBombAnimationComplete = useCallback(() => {
    setHideCenterCardsForAnimation(false);
    setActiveBombEvent(null);
  }, []);

  if (state === undefined) {
    return null;
  }

  return (
    <>
      <div style={roomStyle(chatOpen)}>
        <div style={leave(window.innerWidth < 786)} onClick={leaveRoom}>
          &larr;
        </div>
        <Options
          state={state}
          requestPending={optimisticAction !== null}
          applyPendingTurn={applyPendingTurn}
          applyPendingTakeMiddleCards={applyPendingTakeMiddleCards}
          applyPendingPlayAgain={applyPendingPlayAgain}
          revalidateState={revalidateState}
          revertOptimisticState={revertOptimisticState}
          selectedCards={selectedCards}
          setSelectedCards={setSelectedCards}
        />
        <Center
          state={state}
          hideCards={hideCenterCardsForAnimation}
        />
        <Results state={state} />
        <LeftPerson state={state} />
        <RightPerson state={state} />
        <LeftTurnSignal state={state} hideSignal={activeBombEvent !== null} />
        <RightTurnSignal state={state} hideSignal={activeBombEvent !== null} />
        <Multiplier state={state} />
        <Balance state={state} />
        <Hand
          state={state}
          selectedCards={selectedCards}
          toggleCard={toggleCard}
        />
        {!chatOpen && (
          <div>
            <div
              role="button"
              onClick={() => setChatOpen(true)}
              style={openChat(window.innerWidth < 786)}
            >
              {"<<"} chat
            </div>
          </div>
        )}
        <BombAnimation
          event={activeBombEvent}
          onComplete={handleBombAnimationComplete}
        />
      </div>
      <Chat chatOpen={chatOpen} setChatOpen={setChatOpen} />
    </>
  );
};

const leave = (smallScreen: boolean): CSS.Properties => ({
  position: "absolute",
  top: 0,
  left: 0,
  marginLeft: smallScreen ? "30px" : "50px",
  marginTop: smallScreen ? "20px" : "30px",
  fontSize: "2em",
  cursor: "pointer",
});

const roomStyle = (showChat: boolean): CSS.Properties => ({
  position: "absolute",
  width: showChat ? "80%" : "100%",
  height: "100%",
});

const openChat = (smallScreen: boolean): CSS.Properties => ({
  position: "absolute",
  top: 0,
  right: 0,
  margin: smallScreen ? "30px" : "50px",
  cursor: "pointer",
});

const getPollInterval = (
  state: IUserGetStateReturn | null | undefined,
  hasPendingTurn: boolean,
  pageVisible: boolean,
): number => {
  if (!pageVisible) {
    return 2000;
  }

  if (hasPendingTurn) {
    return 250;
  }

  if (state?.gameIsActive) {
    return 500;
  }

  return 1000;
};

const getDisplayedState = (
  state: IUserGetStateReturn | undefined,
  optimisticAction: OptimisticAction | null,
): IUserGetStateReturn | undefined => {
  if (state === undefined || optimisticAction === null) {
    return state;
  }

  switch (optimisticAction.kind) {
    case "play":
      return projectTurnState(state, optimisticAction.cards);
    case "pass":
      return projectTurnState(state, []);
    case "take":
      return projectTakeMiddleCardsState(state);
    case "playAgain":
      return {
        ...state,
        playAgain: true,
      };
  }
};

type OptimisticAction =
  | {
    kind: "play";
    cards: IGetStateCard[];
    baseFingerprint: string;
  }
  | {
    kind: "pass";
    baseFingerprint: string;
  }
  | {
    kind: "take";
    baseFingerprint: string;
  }
  | {
    kind: "playAgain";
    baseFingerprint: string;
  };

const projectTurnState = (
  state: IUserGetStateReturn,
  playedCards: IGetStateCard[],
): IUserGetStateReturn => {
  if (state.position !== state.turn) {
    return state;
  }

  const nextTurn = (state.turn + 1) % 3;
  const playedCardIDs = new Set(playedCards.map(card => card.id));
  const nextState: IUserGetStateReturn = {
    ...state,
    cards: state.cards.filter(card => !playedCardIDs.has(card.id)),
    lastTwoTurns: [playedCards, ...state.lastTwoTurns].slice(0, 2),
    turn: nextTurn,
    showMiddleCards: false,
  };

  if (shouldBlindTakeAfterPass(state, playedCards)) {
    const landlord = getHandForPosition(state.hands, nextTurn);
    if (landlord !== undefined) {
      nextState.landlordHandID = landlord.handID;
      nextState.multiplier = state.multiplier * 2;
    }
  }

  return nextState;
};

const projectTakeMiddleCardsState = (
  state: IUserGetStateReturn,
): IUserGetStateReturn => {
  const currentHand = getHandForPosition(state.hands, state.position);
  if (currentHand === undefined) {
    return state;
  }

  return {
    ...state,
    landlordHandID: currentHand.handID,
    showMiddleCards: true,
    middleCards: state.middleCards.length > 0 ? state.middleCards : createPlaceholderMiddleCards(),
  };
};

const shouldBlindTakeAfterPass = (
  state: IUserGetStateReturn,
  playedCards: IGetStateCard[],
): boolean => {
  return (
    playedCards.length === 0
    && state.landlordHandID === null
    && state.lastTwoTurns[0] !== undefined
    && state.lastTwoTurns[0].length === 0
  );
};

const createPlaceholderMiddleCards = (): IGetStateCard[] => {
  return [0, 1, 2].map(index => ({
    id: -(index + 1),
    rank: -1,
    value: "X",
  }));
};

const getStateFingerprint = (state: IUserGetStateReturn): string => {
  return JSON.stringify({
    turn: state.turn,
    position: state.position,
    landlordHandID: state.landlordHandID,
    showMiddleCards: state.showMiddleCards,
    playAgain: state.playAgain,
    multiplier: state.multiplier,
    cards: state.cards.map(card => card.id),
    middleCards: state.middleCards.map(card => card.id),
    lastTwoTurns: state.lastTwoTurns.map(turn => turn.map(card => card.id)),
    results: state.results.map(result => `${result.displayName}:${result.change}`),
    hands: state.hands.map(hand =>
      `${hand.position}:${hand.cardsLeft ?? "n"}:${hand.dou ? 1 : 0}:${hand.handID}`
    ),
  });
};

const isBombCards = (cards: IGetStateCard[]): boolean => {
  if (cards.length === 2) {
    const values = cards.map(card => card.value).sort();
    return values[0] === "BJ" && values[1] === "LJ";
  }

  if (cards.length !== 4) {
    return false;
  }

  const [firstCard] = cards;
  if (firstCard === undefined) {
    return false;
  }

  return cards.every(card => card.value === firstCard.value);
};

const getBombKey = (cards: IGetStateCard[]): string => {
  return cards.map(card => card.id).sort((left, right) => left - right).join("-");
};

const getLatestBombCandidate = (
  lastTwoTurns: IGetStateCard[][],
): { cards: IGetStateCard[] } | null => {
  const latestTurn = lastTwoTurns[0];
  if (latestTurn !== undefined && isBombCards(latestTurn)) {
    return {
      cards: latestTurn,
    };
  }

  const previousTurn = lastTwoTurns[1];
  if (
    latestTurn !== undefined
    && latestTurn.length === 0
    && previousTurn !== undefined
    && isBombCards(previousTurn)
  ) {
    return {
      cards: previousTurn,
    };
  }

  return null;
};
